import { useCallback, useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import { ClientSummary, Distributor, DocumentMeta, Fund, FundConfig, GenerateResult, TemplateInfo } from '../api/types'
import { useOpsApi } from '../api/context'
import { apiError } from '../api/errors'
import { saveDocumentFile } from '../api/download'
import { useCurrentUser } from '../auth'
import { InfoHint } from '../components/InfoHint'
import { PageHeader } from '../components/PageHeader'
import { StatusChip } from '../components/StatusChip'
import {
  parseAmount,
  validateAccountNumber,
  validateAmount,
  validateCompanyId,
  validateEmail,
  validatePersonalNumber,
} from '../validation'
import { ApexInvestmentSection } from './generovani/ApexInvestmentSection'
import { ClientDocumentsCard } from './generovani/ClientDocumentsCard'
import { ClientSection } from './generovani/ClientSection'
import { DEFAULT_GENERATION_OPTIONS, GenerationOptions, GenerationOptionsState } from './generovani/GenerationOptions'
import { ImperiumDocumentsSection } from './generovani/ImperiumDocumentsSection'
import {
  ApexFormState,
  ClientFormState,
  ClientMode,
  EMPTY_APEX_FORM,
  EMPTY_CLIENT_FORM,
  clientToForm,
  formToClientInput,
} from './generovani/formState'

/** Skloňování počtu dokumentů v potvrzovací hlášce. */
function pocetDokumentu(n: number): string {
  if (n === 1) return 'dokument'
  return n <= 4 ? 'dokumenty' : 'dokumentů'
}

const CZK = new Intl.NumberFormat('cs-CZ', {
  style: 'currency',
  currency: 'CZK',
  maximumFractionDigits: 0,
})

/**
 * Fáze 2 — Generování smluv: nový/stávající klient → validace → mock generování
 * dokumentů. Apex navíc vytvoří vklad WAITING_PAYMENT (vstup do fronty plateb),
 * Imperium jen dokumenty (bez fondu). Založení klienta je plnohodnotné.
 */
export function GenerovaniPhase() {
  const api = useOpsApi()
  const user = useCurrentUser()
  const isApex = user.territory === 'APEX'

  const [mode, setMode] = useState<ClientMode>('NEW')
  const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(null)
  const [form, setForm] = useState<ClientFormState>(EMPTY_CLIENT_FORM)
  const [options, setOptions] = useState<GenerationOptionsState>(DEFAULT_GENERATION_OPTIONS)
  const [distributors, setDistributors] = useState<Distributor[]>([])
  const [apexState, setApexState] = useState<ApexFormState>(EMPTY_APEX_FORM)
  const [selectedDocKeys, setSelectedDocKeys] = useState<string[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState<GenerateResult | null>(null)
  const [clientDocuments, setClientDocuments] = useState<DocumentMeta[]>([])

  const [funds, setFunds] = useState<Fund[]>([])
  const [templates, setTemplates] = useState<TemplateInfo[]>([])
  // Hlavní vypínač e-mailů (nastavení modulu) — u zaškrtávátek upozorní, že e-maily neodejdou.
  const [config, setConfig] = useState<FundConfig | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .getConfig()
      .then(loaded => {
        if (!cancelled) setConfig(loaded)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [api])

  useEffect(() => {
    let cancelled = false
    if (isApex) {
      api.listFunds().then(loaded => {
        if (!cancelled) setFunds(loaded)
      })
      api.listDistributors().then(loaded => {
        // Interní (bez role) a neaktivní nenabízet. Starší BE roli ani aktivitu nevrací → jen podle jména.
        if (!cancelled)
          setDistributors(
            loaded.filter(d =>
              d.roleId === undefined ? !/intern/i.test(d.name) : Boolean(d.roleId) && d.active !== false
            )
          )
      })
    } else {
      api.listImperiumTemplates().then(loaded => {
        if (!cancelled) setTemplates(loaded)
      })
    }
    return () => {
      cancelled = true
    }
  }, [api, isApex])

  const resetAll = useCallback(() => {
    setSelectedClient(null)
    setForm(EMPTY_CLIENT_FORM)
    setOptions(DEFAULT_GENERATION_OPTIONS)
    setApexState(EMPTY_APEX_FORM)
    setSelectedDocKeys([])
    setErrors({})
    setSubmitError(null)
    setResult(null)
    setClientDocuments([])
  }, [])

  // Přepnutí tenant v dev harnessu — rozdílná pole i výstupy, formulář od nuly.
  useEffect(() => {
    resetAll()
    setMode('NEW')
  }, [isApex, resetAll])

  const handleModeChange = (next: ClientMode) => {
    setMode(next)
    resetAll()
  }

  const handleSelectExisting = (summary: ClientSummary | null) => {
    setSelectedClient(summary)
    setResult(null)
    setErrors({})
    setSubmitError(null)
    if (!summary) {
      setForm(EMPTY_CLIENT_FORM)
      setClientDocuments([])
      return
    }
    api.getClient(summary.id).then(client => setForm(clientToForm(client)))
    api.listClientDocuments(summary.id).then(setClientDocuments)
  }

  const handleFormChange = (patch: Partial<ClientFormState>) => {
    setForm(prev => ({ ...prev, ...patch }))
  }

  const validate = (): Record<string, string> => {
    const found: Record<string, string> = {}

    if (mode === 'EXISTING' && !selectedClient) {
      found.selectedClient = 'Vyberte klienta'
      return found
    }

    if (form.company) {
      if (!form.companyName.trim()) found.companyName = 'Povinné pole'
      if (!form.companyId.trim()) found.companyId = 'Povinné pole'
      else {
        const icoError = validateCompanyId(form.companyId)
        if (icoError) found.companyId = icoError
      }
    } else {
      if (!form.surname.trim()) found.surname = 'Povinné pole'
      if (!form.personalNumber.trim()) found.personalNumber = 'Povinné pole'
      else {
        const rcError = validatePersonalNumber(form.personalNumber)
        if (rcError) found.personalNumber = rcError
      }
    }

    if (!form.email.trim()) found.email = 'Povinné pole'
    else {
      const emailError = validateEmail(form.email)
      if (emailError) found.email = emailError
    }
    if (!form.street.trim()) found.street = 'Povinné pole'
    if (!form.town.trim()) found.town = 'Povinné pole'
    if (!form.zipCode.trim()) found.zipCode = 'Povinné pole'

    if (isApex) {
      if (!apexState.strategy) found.strategy = 'Vyberte strategii'
      if (!apexState.variant) found.variant = 'Vyberte variantu'
      const amountError = validateAmount(apexState.amountText)
      if (amountError) found.amount = amountError
      const accountError = validateAccountNumber(apexState.accountNumber)
      if (accountError) found.accountNumber = accountError
      if (apexState.entryFeePercentText.trim()) {
        const fee = parseAmount(apexState.entryFeePercentText)
        if (fee === null || fee < 0 || fee > 100) {
          found.entryFeePercent = 'Zadejte procento 0–100'
        }
      }
    } else if (selectedDocKeys.length === 0) {
      found.documents = 'Vyberte alespoň jeden dokument'
    }

    return found
  }

  const handleGenerate = async () => {
    const found = validate()
    setErrors(found)
    setSubmitError(null)
    if (Object.keys(found).length > 0) return

    setGenerating(true)
    try {
      const client = formToClientInput(form)
      const clientId = mode === 'EXISTING' ? selectedClient?.id : undefined
      // Přístup + uvítací e-mail jen pro nového klienta, kterého zakládáme.
      const credentials = mode === 'NEW' && options.createClient && options.sendCredentials
      const mails = { sendCredentials: credentials, sendWelcomeEmail: credentials }

      const generated = isApex
        ? await api.generateApexDocuments({
            clientId,
            client,
            strategy: apexState.strategy as Exclude<ApexFormState['strategy'], ''>,
            variant: apexState.variant as Exclude<ApexFormState['variant'], ''>,
            amount: parseAmount(apexState.amountText) ?? 0,
            accountNumber: apexState.accountNumber.trim(),
            entryFeePercent: parseAmount(apexState.entryFeePercentText) ?? 0,
            entryFeeMode: apexState.entryFeeMode,
            ...mails,
            createClient: mode === 'NEW' ? options.createClient : undefined,
            distributorId:
              mode === 'NEW' && options.createClient && options.distributorId ? options.distributorId : undefined,
          })
        : await api.generateImperiumDocuments({
            clientId,
            client,
            documentKeys: selectedDocKeys,
            sendCredentials: false,
            sendWelcomeEmail: false,
          })

      setResult(generated)

      // Imperium s novým člověkem klienta nezakládá — dokumenty se rovnou stáhnou
      // a nikam se neukládají. Formulář proto zůstává, jak byl.
      if (!generated.client) {
        if (generated.file) {
          saveDocumentFile(generated.file)
        }
        return
      }

      // Klient teď existuje — formulář pokračuje v režimu STÁVAJÍCÍ nad ním.
      setMode('EXISTING')
      setSelectedClient({
        id: generated.client.id,
        fullName: generated.client.fullName,
        personalNumber: generated.client.personalNumber,
        companyId: generated.client.companyId,
        email: generated.client.email,
      })
      setForm(clientToForm(generated.client))
      setClientDocuments(await api.listClientDocuments(generated.client.id))
    } catch (error) {
      setSubmitError(apiError(error, 'Generování selhalo'))
    } finally {
      setGenerating(false)
    }
  }

  const showDocuments = clientDocuments.length > 0 || (mode === 'EXISTING' && selectedClient)

  return (
    <Box sx={{ maxWidth: 1000, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Generování smluv"
        subtitle={
          isApex
            ? 'Vygenerování smlouvy, založení klienta a vkladu do fronty plateb.'
            : 'Vygenerování klientské dokumentace a založení klienta.'
        }
        info={
          <InfoHint
            title="Co se stane po vygenerování"
            lines={
              isApex
                ? [
                    'Nový klient se založí v Portiliu (chybí-li povinné údaje, jako „nedokončený" — doplní se na kartě klienta).',
                    'Vygeneruje se PDF smlouva dle strategie a varianty a uloží se do archivu dokumentů klienta.',
                    'Varianta smlouvy určuje třídu poplatků: EB → třída A (nižší sazby), NORM a Dohoda → třída B.',
                    'Vznikne vklad ve stavu ČEKÁ NA PLATBU s QR podklady — pokračuje ve frontě Vklady / výběry.',
                    'Zaškrtnuté emaily (přihlašovací údaje, uvítací) se posílají až po úspěšném vygenerování a jen novému klientovi.',
                  ]
                : [
                    'Nový klient se založí v Portiliu (chybí-li povinné údaje, jako „nedokončený" — doplní se na kartě klienta).',
                    'Vybrané dokumenty se vygenerují do ZIP a uloží do archivu dokumentů klienta.',
                    'Imperium nemá fond — žádný vklad nevzniká.',
                    'Zaškrtnuté emaily (přihlašovací údaje, uvítací) se posílají až po úspěšném vygenerování a jen novému klientovi.',
                  ]
            }
          />
        }
      />

      <ClientSection
        showImperiumFields={!isApex}
        mode={mode}
        onModeChange={handleModeChange}
        selectedClient={selectedClient}
        onSelectExisting={handleSelectExisting}
        form={form}
        onFormChange={handleFormChange}
        errors={errors}
      />

      {isApex ? (
        <ApexInvestmentSection
          funds={funds}
          state={apexState}
          onChange={patch => setApexState(prev => ({ ...prev, ...patch }))}
          errors={errors}
        />
      ) : (
        <ImperiumDocumentsSection
          templates={templates}
          selectedKeys={selectedDocKeys}
          onChange={setSelectedDocKeys}
          error={errors.documents}
        />
      )}

      {submitError && <Alert severity="error">{submitError}</Alert>}

      {result && (
        <Alert severity="success">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {result.client
              ? `Vygenerováno: ${result.documents.length} ${pocetDokumentu(result.documents.length)} pro klienta ${
                  result.client.fullName
                }.`
              : 'Dokumenty vygenerovány a staženy. Klient se do Portilia nezakládá — dokumenty nikde nezůstávají.'}
          </Typography>
          {result.deposit && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
              <StatusChip status={result.deposit.status} />
              <Typography variant="body2">
                Vklad {CZK.format(result.deposit.amount)} · {result.deposit.strategy} · účet{' '}
                {result.deposit.accountNumber} — zařazen do fronty plateb.
              </Typography>
            </Box>
          )}
          {result.credentialsSent && <Typography variant="body2">Přihlašovací údaje byly odeslány.</Typography>}
          {result.welcomeEmailSent && <Typography variant="body2">Uvítací email byl odeslán.</Typography>}
        </Alert>
      )}

      {result && (result.credentialsSent === false || result.welcomeEmailSent === false) && (
        <Alert severity="warning">
          Emaily klientovi se nepodařilo odeslat
          {result.clientEmailError ? ` (${result.clientEmailError})` : ''}. Dokumenty jsou vygenerované.
        </Alert>
      )}

      {isApex && (
        <GenerationOptions
          options={options}
          onChange={setOptions}
          isNewClient={mode === 'NEW'}
          distributors={distributors}
          emailsDisabled={config ? !config.emailsEnabled : false}
          disabled={generating}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button variant="contained" size="large" onClick={handleGenerate} disabled={generating}>
          {isApex ? 'Vygenerovat smlouvy' : 'Vygenerovat dokumenty'}
        </Button>
        <Button variant="text" color="inherit" onClick={() => handleModeChange(mode)} disabled={generating}>
          Vyčistit formulář
        </Button>
        {generating && <CircularProgress size={22} />}
      </Box>

      {showDocuments && (
        <ClientDocumentsCard
          title="Dokumenty klienta"
          documents={clientDocuments}
          highlightIds={result?.documents.map(d => d.id)}
        />
      )}
    </Box>
  )
}
